import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Package, Zap, GitMerge, Wrench, Users, Percent, Download, ArrowRight, AlertTriangle } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

// Long-form guide aimed at the generic "sheet metal costing" /
// "κοστολόγηση λαμαρίνας" searches: explains the method, works an example,
// then links out to the feature pages for each cost component.
const translations = {
  el: {
    hero: {
      title: 'Κοστολόγηση Λαμαρίνας: Πώς Υπολογίζεται το Κόστος ενός Εξαρτήματος',
      subtitle: 'Ένας πρακτικός οδηγός για μεταλλουργεία: από τι αποτελείται το κόστος ενός κομματιού λαμαρίνας, πώς το υπολογίζετε βήμα-βήμα και πώς να το κάνετε σε δευτερόλεπτα αντί για ώρες.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε τις Λειτουργίες',
    },
    intro: {
      title: 'Τι είναι η κοστολόγηση λαμαρίνας',
      body: [
        'Κοστολόγηση λαμαρίνας είναι ο υπολογισμός του πραγματικού κόστους παραγωγής ενός εξαρτήματος από λαμαρίνα — πριν δώσετε τιμή στον πελάτη. Μια σωστή κοστολόγηση λαμβάνει υπόψη το υλικό και τη φύρα, την κοπή (laser, πλάσμα ή ψαλίδι), τη στράντζα, το σετάρισμα των μηχανημάτων, την εργασία και τα γενικά έξοδα, και πάνω σε αυτά το περιθώριο κέρδους.',
        'Αν η κοστολόγηση γίνεται «με το μάτι» ή με ένα πρόχειρο Excel, είτε χάνετε δουλειές επειδή είστε ακριβοί, είτε κερδίζετε δουλειές που δεν βγάζουν χρήματα. Η μέθοδος παρακάτω είναι η ίδια που χρησιμοποιούν τα περισσότερα εργαστήρια — και αυτή που αυτοματοποιεί το Workshop Assistant.',
      ],
    },
    components: {
      title: 'Από τι αποτελείται το κόστος',
      items: [
        { icon: 'material', title: 'Υλικό & Φύρα', description: 'Βάρος = επιφάνεια × πάχος × πυκνότητα (χάλυβας ≈ 7.850 kg/m³). Προσθέστε τη φύρα του nesting — συνήθως 10–25% — και πολλαπλασιάστε με την τιμή ανά κιλό.', link: '/production-costing' },
        { icon: 'cut', title: 'Κοπή', description: 'Μήκος κοπής (περίμετρος + οπές) ÷ ταχύτητα κοπής για το υλικό και το πάχος, συν χρόνος διάτρησης ανά οπή, × ωριαίο κόστος μηχανής.', link: '/laser-costing' },
        { icon: 'bend', title: 'Στράντζα', description: 'Αριθμός στραντζαρισμάτων × χρόνος ανά στραντζάρισμα × ωριαίο κόστος πρέσας. Τα μεγάλα ή βαριά κομμάτια χρειάζονται περισσότερο χρόνο χειρισμού.', link: '/bend-costing' },
        { icon: 'setup', title: 'Σετάρισμα', description: 'Ο χρόνος προετοιμασίας κάθε μηχανής (προγράμματα, εργαλεία, πρώτο κομμάτι) μοιράζεται σε όλα τα τεμάχια της παρτίδας.', link: '/production-costing' },
        { icon: 'labor', title: 'Εργασία & Γενικά Έξοδα', description: 'Ηλεκτρικό ρεύμα, αέρια, εργατοώρες, συγκόλληση, βαφή και μεταφορά — ό,τι δεν φαίνεται στο σχέδιο αλλά πληρώνεται.', link: '/production-costing' },
        { icon: 'margin', title: 'Περιθώριο Κέρδους', description: 'Το ποσοστό που προσθέτετε πάνω στο κόστος. Χωρίς σωστό κόστος από κάτω, το περιθώριο είναι απλώς μια εικασία.', link: '/quotation-software' },
      ],
    },
    formula: {
      title: 'Ο τύπος',
      lines: [
        'Κόστος τεμαχίου = Υλικό + Κοπή + Στράντζα + (Σετάρισμα ÷ Ποσότητα) + Εργασία',
        'Τιμή τεμαχίου = Κόστος τεμαχίου × (1 + Περιθώριο κέρδους)',
      ],
    },
    example: {
      title: 'Παράδειγμα: βάση από λαμαρίνα 2 mm',
      intro: 'Μια βάση από μαύρη λαμαρίνα 2 mm, ανάπτυγμα 300 × 200 mm, με 4 στραντζαρίσματα, παρτίδα 50 τεμαχίων. Οι τιμές είναι ενδεικτικές — βάλτε τις δικές σας.',
      head: ['Στοιχείο', 'Υπολογισμός', 'Ανά τεμάχιο'],
      rows: [
        ['Υλικό', '0,06 m² × 2 mm × 7.850 kg/m³ = 0,94 kg, +15% φύρα = 1,08 kg × 1,20 €/kg', '1,30 €'],
        ['Κοπή laser', '1,2 m κοπή ÷ 4 m/min = 0,3 min × 1,50 €/min', '0,45 €'],
        ['Στράντζα', '4 στραντζαρίσματα × 12 s = 0,8 min × 1,00 €/min', '0,80 €'],
        ['Σετάρισμα', '40 € (laser + στράντζα) ÷ 50 τεμάχια', '0,80 €'],
      ],
      subtotal: ['Κόστος τεμαχίου', '', '3,35 €'],
      total: ['Τιμή με 25% περιθώριο', '3,35 € × 1,25', '4,19 €'],
      outro: 'Για την παρτίδα των 50 τεμαχίων η προσφορά είναι περίπου 209 €. Αν ο πελάτης ζητήσει 10 τεμάχια, το σετάρισμα ανεβαίνει στα 4,00 € ανά τεμάχιο και η τιμή στα 8,19 € — γι\' αυτό η ποσότητα πρέπει πάντα να μπαίνει στον υπολογισμό.',
    },
    mistakes: {
      title: 'Συνηθισμένα λάθη στην κοστολόγηση',
      items: [
        'Υπολογισμός υλικού μόνο από το καθαρό βάρος, χωρίς φύρα.',
        'Ίδιο σετάρισμα για 5 και για 500 τεμάχια.',
        'Μέτρηση μόνο της εξωτερικής περιμέτρου και όχι των οπών και των διατρήσεων.',
        'Παλιές τιμές υλικού που δεν ενημερώνονται όταν αλλάζει η αγορά.',
        'Χειροκίνητη μέτρηση σχεδίων, που κοστίζει χρόνο και φέρνει λάθη.',
      ],
    },
    automate: {
      title: 'Πώς το κάνει αυτόματα το Workshop Assistant',
      body: 'Εισάγετε το αρχείο DXF ή STEP και το Workshop Assistant μετρά μόνο του επιφάνεια, βάρος, μήκος κοπής, οπές και στραντζαρίσματα. Με βάση τις τιμές υλικών και τα κόστη μηχανημάτων που έχετε ορίσει μία φορά, βγάζει το κόστος κάθε εξαρτήματος και την τελική προσφορά σε δευτερόλεπτα — με αναφορά Excel έτοιμη για τον πελάτη.',
      link: 'Δείτε πώς γίνεται η εισαγωγή DXF & STEP',
    },
    faq: [
      { q: 'Πώς υπολογίζεται το κόστος της λαμαρίνας;', a: 'Προσθέτετε το κόστος υλικού (βάρος + φύρα × τιμή ανά κιλό), το κόστος κοπής, το κόστος στράντζας, το σετάρισμα μοιρασμένο στην ποσότητα και την εργασία. Στο τελικό κόστος προσθέτετε το περιθώριο κέρδους.' },
      { q: 'Πόση φύρα να υπολογίζω στη λαμαρίνα;', a: 'Συνήθως 10–25%, ανάλογα με το σχήμα των κομματιών και πόσο καλά «κάθονται» στο φύλλο. Το nesting του Workshop Assistant δείχνει την πραγματική αξιοποίηση του φύλλου.' },
      { q: 'Πώς κοστολογείται η κοπή laser;', a: 'Από το συνολικό μήκος κοπής και τον αριθμό διατρήσεων, τη ταχύτητα κοπής για το συγκεκριμένο υλικό και πάχος, και το ωριαίο κόστος του μηχανήματος.' },
      { q: 'Υπάρχει πρόγραμμα κοστολόγησης λαμαρίνας στα ελληνικά;', a: 'Ναι. Το Workshop Assistant είναι πλήρως στα ελληνικά, λειτουργεί σε Windows και Android και έχει δωρεάν δοκιμή 5 ημερών χωρίς πιστωτική κάρτα.' },
    ],
    cta: { title: 'Κοστολογήστε το Επόμενο Κομμάτι σε Δευτερόλεπτα', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή — 5 ημέρες δωρεάν, χωρίς πιστωτική κάρτα.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Sheet Metal Costing: How to Calculate the Cost of a Sheet Metal Part',
      subtitle: 'A practical guide for fabrication shops: what goes into the cost of a sheet metal part, how to calculate it step by step, and how to do it in seconds instead of hours.',
      cta1: 'Download Free Trial',
      cta2: 'Explore Features',
    },
    intro: {
      title: 'What is sheet metal costing?',
      body: [
        'Sheet metal costing is working out the real manufacturing cost of a sheet metal part before you quote it. A sound costing covers material and scrap, cutting (laser, plasma or guillotine), bending, machine setup, labor and overheads — and only then adds your margin.',
        'When costing is done by gut feel or a quick spreadsheet, you either lose jobs because you are too expensive or win jobs that lose money. The method below is the one most shops use — and the one Workshop Assistant automates.',
      ],
    },
    components: {
      title: 'What goes into the cost',
      items: [
        { icon: 'material', title: 'Material & Scrap', description: 'Weight = area × thickness × density (steel ≈ 7,850 kg/m³). Add nesting scrap — typically 10–25% — and multiply by the price per kg.', link: '/production-costing' },
        { icon: 'cut', title: 'Cutting', description: 'Cut length (outer perimeter + holes) ÷ cutting speed for that material and thickness, plus pierce time per hole, × machine hourly rate.', link: '/laser-costing' },
        { icon: 'bend', title: 'Bending', description: 'Number of bends × time per bend × press brake hourly rate. Large or heavy parts need extra handling time.', link: '/bend-costing' },
        { icon: 'setup', title: 'Setup', description: 'Each machine\'s preparation time (programs, tooling, first-off part) is spread across every part in the batch.', link: '/production-costing' },
        { icon: 'labor', title: 'Labor & Overheads', description: 'Electricity, gas, labor hours, welding, painting and delivery — everything the drawing doesn\'t show but you still pay for.', link: '/production-costing' },
        { icon: 'margin', title: 'Margin', description: 'The percentage you add on top of cost. Without an accurate cost underneath, the margin is just a guess.', link: '/quotation-software' },
      ],
    },
    formula: {
      title: 'The formula',
      lines: [
        'Part cost = Material + Cutting + Bending + (Setup ÷ Quantity) + Labor',
        'Part price = Part cost × (1 + Margin)',
      ],
    },
    example: {
      title: 'Worked example: a 2 mm steel bracket',
      intro: 'A mild steel bracket, 2 mm thick, 300 × 200 mm flat blank, 4 bends, batch of 50. The rates are illustrative — plug in your own.',
      head: ['Item', 'Calculation', 'Per part'],
      rows: [
        ['Material', '0.06 m² × 2 mm × 7,850 kg/m³ = 0.94 kg, +15% scrap = 1.08 kg × €1.20/kg', '€1.30'],
        ['Laser cutting', '1.2 m cut ÷ 4 m/min = 0.3 min × €1.50/min', '€0.45'],
        ['Bending', '4 bends × 12 s = 0.8 min × €1.00/min', '€0.80'],
        ['Setup', '€40 (laser + press brake) ÷ 50 parts', '€0.80'],
      ],
      subtotal: ['Part cost', '', '€3.35'],
      total: ['Price at 25% margin', '€3.35 × 1.25', '€4.19'],
      outro: 'For the batch of 50 that is a quote of about €209. If the customer asks for 10 parts instead, setup rises to €4.00 per part and the price to €8.19 — which is why quantity must always be part of the calculation.',
    },
    mistakes: {
      title: 'Common sheet metal costing mistakes',
      items: [
        'Pricing material from net part weight only, with no scrap allowance.',
        'Using the same setup cost for 5 parts and for 500.',
        'Measuring the outer perimeter only and forgetting holes and pierces.',
        'Stale material prices that aren\'t updated when the market moves.',
        'Measuring drawings by hand — slow, and a source of errors.',
      ],
    },
    automate: {
      title: 'How Workshop Assistant does it for you',
      body: 'Import a DXF or STEP file and Workshop Assistant measures area, weight, cut length, holes and bends on its own. Using the material prices and machine rates you set once, it produces the cost of every part and the final quote in seconds — with an Excel report ready for your customer.',
      link: 'See how DXF & STEP import works',
    },
    faq: [
      { q: 'How do you calculate sheet metal cost?', a: 'Add material cost (weight plus scrap × price per kg), cutting cost, bending cost, setup cost divided by quantity, and labor. Then add your margin on top of the total cost.' },
      { q: 'How much scrap should I allow for sheet metal?', a: 'Typically 10–25%, depending on part shape and how well parts nest on the sheet. Workshop Assistant\'s nesting shows actual sheet utilization.' },
      { q: 'How is laser cutting cost calculated?', a: 'From total cut length and number of pierces, the cutting speed for that material and thickness, and the machine\'s hourly rate.' },
      { q: 'Is there software for sheet metal costing?', a: 'Yes. Workshop Assistant is sheet metal costing software for Windows and Android, with DXF/STEP import, nesting and Excel quotes, and a 5-day free trial with no credit card.' },
    ],
    cta: { title: 'Cost Your Next Part in Seconds', subtitle: 'Download the free trial — 5 days free, no credit card required.', btn: 'Download Free Trial' },
  },
}

const icons = {
  material: <Package className="h-10 w-10" />,
  cut: <Zap className="h-10 w-10" />,
  bend: <GitMerge className="h-10 w-10" />,
  setup: <Wrench className="h-10 w-10" />,
  labor: <Users className="h-10 w-10" />,
  margin: <Percent className="h-10 w-10" />,
}

const SheetMetalCosting = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Sheet Metal Costing — How to Calculate Part Cost (Guide)"
        description="How to calculate sheet metal part cost: material and scrap, laser cutting, bending, setup and margin, with a worked example and formula. Plus software that does it automatically."
        path="/sheet-metal-costing"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/features" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
          {t.intro.body.map((p, i) => (
            <p key={i} className="text-lg text-gray-600 mb-4">{p}</p>
          ))}
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.components.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.components.items.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={c.link} className="card group hover:border-primary-500 border-2 border-transparent block h-full">
                <div className="text-primary-600 mb-4">{icons[c.icon]}</div>
                <h3 className="text-xl font-semibold mb-2">{c.title}</h3>
                <p className="text-gray-600">{c.description}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">{t.formula.title}</h2>
          <div className="rounded-xl bg-gray-900 text-white p-6 mb-12 space-y-2 font-mono text-sm md:text-base">
            {t.formula.lines.map((line, i) => <p key={i}>{line}</p>)}
          </div>

          <h2 className="text-3xl font-bold mb-4">{t.example.title}</h2>
          <p className="text-lg text-gray-600 mb-6">{t.example.intro}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  {t.example.head.map((h, i) => (
                    <th key={i} className={`py-3 pr-4 font-semibold ${i === 2 ? 'text-right' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.example.rows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium whitespace-nowrap">{row[0]}</td>
                    <td className="py-3 pr-4 text-gray-600 text-sm">{row[1]}</td>
                    <td className="py-3 text-right tabular-nums whitespace-nowrap">{row[2]}</td>
                  </tr>
                ))}
                {[t.example.subtotal, t.example.total].map((row, i) => (
                  <tr key={i} className={i ? 'bg-primary-50 font-bold' : 'border-b border-gray-200 font-semibold'}>
                    <td className="py-3 pr-4 pl-1 whitespace-nowrap">{row[0]}</td>
                    <td className="py-3 pr-4 text-gray-600 text-sm font-normal">{row[1]}</td>
                    <td className="py-3 pr-1 text-right tabular-nums whitespace-nowrap">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-lg text-gray-600 mt-6">{t.example.outro}</p>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">{t.mistakes.title}</h2>
          <ul className="space-y-3">
            {t.mistakes.items.map((m, i) => (
              <li key={i} className="flex items-start gap-3 text-lg text-gray-700">
                <AlertTriangle className="h-5 w-5 text-primary-600 mt-1 flex-shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">{t.automate.title}</h2>
          <p className="text-lg text-gray-600 mb-4">{t.automate.body}</p>
          <Link to="/dxf-step-import" className="inline-flex items-center gap-2 font-semibold text-primary-600 hover:text-primary-700">
            {t.automate.link}<ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default SheetMetalCosting
